import { useDerivationStatus } from '@/hooks/queries/useSchwab';

type DerivationJob = 'sweep' | 'pnl' | 'capture' | 'stock' | 'reconcile';

const JOB_LABELS: Record<DerivationJob, string> = {
  sweep: 'closing expired legs',
  pnl: 'recalculating P&L',
  capture: 'journal capture',
  stock: 'stock positions',
  reconcile: 'reconciling',
};

export interface RebuildingBannerViewProps {
  /** The API's narrower signal: sweep / pnl / capture still queued. NOT `pending` — see below. */
  rebuild: boolean;
  jobs: DerivationJob[];
}

/**
 * Props-driven banner shown while an import or Tools replay's derivation queue is still draining.
 * Those routes rebuild every chain/position/leg in frugal mode and leave the expiry sweep, P&L
 * recalc and journal capture to a cron queue that drains one job per 5-minute tick — until it
 * does, historical expired legs read as open and P&L reads zero, which is what the banner warns
 * about.
 *
 * It keys off `rebuild`, not `pending`: a routine sync enqueues only `stock` + `reconcile`, which
 * is pending work (the hook keeps polling, and invalidates when it clears) but leaves nothing on
 * the page reading wrong meanwhile. Banner-on-pending would show an amber "positions may look
 * wrong" warning over a correct page six times a day.
 */
export function RebuildingBannerView({ rebuild, jobs }: RebuildingBannerViewProps) {
  if (!rebuild) return null;

  const remaining = jobs.map((job) => JOB_LABELS[job] ?? job).join(', ');

  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
    >
      <span className="mt-0.5 inline-block h-2.5 w-2.5 flex-shrink-0 animate-pulse rounded-full bg-amber-500" aria-hidden="true" />
      <div>
        <p className="font-medium">Rebuilding positions after sync</p>
        <p className="text-amber-700">
          Expired legs and P&amp;L may look wrong for a few minutes. Still to run: {remaining}.
        </p>
      </div>
    </div>
  );
}

/** Container: owns the query. Pages render this; tests render the View. */
export function RebuildingBanner() {
  const { data } = useDerivationStatus();
  return <RebuildingBannerView rebuild={data?.rebuild ?? false} jobs={data?.jobs ?? []} />;
}
